// ═══════════════════════════════════════════════════════════════════════════
// FILE: src/app/components/ask-panel/ask-panel.component.ts
// DATE: 2026-10-12
// CREATOR: PIA (Rolle HUGO), beauftragt vom PO am 26.09.2026 / Auftrag 11.10.2026
// ceeq.it: _40 #223 Anfrage per @ (T2, T4) · _40 #218 Textarea Alt+P
//
// PURPOSE
//   Die Textarea, die auf jeder Seite mit Alt+P aufgeht. Drei Reiter:
//     Ask          Anfrage an eine KI oder einen Menschen des pslot, per @
//     For me       offene Anfragen an mich: Reply · Done · Obsolete
//     Waiting for  meine offenen Anfragen und der Stand je Empfänger
//   Unten links sitzt ein Knopf mit Zähler. Er blinkt, solange Anfragen an mich offen sind.
//
// ABLAUF „ASK“
//   pslot und Bezug kommen aus der aktuellen Adresse (/project/<pslot>/item/<id> …).
//   Ohne Element auf der Seite ist der Bezug „n/a“ → Ankertyp pslot, id 0.
//   @ öffnet die Auswahl (GET /api/pslot-addressees); gewählt wird das Handle.
//   Gesendet wird ein Diskussionsbeitrag mit den drei CFLX-Aussagen; der Appell trägt
//   die Nennungen. Die Nachrichtenschicht stellt ihn an jeden Genannten als must_act zu.
//
// ENTWURF (23.10.2026): kommt der Nutzer über „Delegate“ aus dem Eisenhower-Fenster (Alt+N),
//   liegt in req.draft() ein Entwurf. Er überschreibt den Kontext aus der Adresse; der
//   Empfänger wird über die Ansprechbaren des pslots aufgelöst (Rolle → Handle, uuid → Handle).
//
// TESTIDS: ask.* (D4) — Liste im Template.
// ═══════════════════════════════════════════════════════════════════════════

import { Component, ElementRef, HostListener, ViewChild, computed, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { AgentStatusService, AgentStatus } from '../../services/agent-status.service';
import {
  Addressee, AskDraft, OpenRequest, PanelTab, RequestsService, WaitingRequest, ageOf, anchorLink,
} from '../../services/requests.service';

const ANCHOR_TYPES = ['item', 'task', 'sprint', 'milestone', 'pslot'] as const;
type AnchorType = typeof ANCHOR_TYPES[number];
const MENTION_AT_CARET = /(^|[\s(,;"])@([a-z0-9._-]*)$/i;
const MENTION_ALL = /(?<![\w.@-])@([a-zA-Z0-9][a-zA-Z0-9._-]{1,63})/g;

@Component({
  selector: 'app-ask-panel',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './ask-panel.component.html',
  styleUrls: ['./ask-panel.component.scss'],
})
export class AskPanelComponent {
  readonly req = inject(RequestsService);
  /** 2026-11-02 PIA (HUGO): Ampel je Agent in der @-Auswahl — ist die KI wach? */
  readonly agents = inject(AgentStatusService);
  private router = inject(Router);
  private http = inject(HttpClient);

  @ViewChild('appealBox') appealBox?: ElementRef<HTMLTextAreaElement>;

  readonly anchorTypes = ANCHOR_TYPES;
  readonly ageOf = ageOf;
  readonly anchorLink = anchorLink;

  // ── Formular „Ask“ ─────────────────────────────────────────────────────
  pslot = signal('');
  anchorType = signal<AnchorType>('pslot');
  anchorId = signal<number | null>(null);
  appeal = signal('');
  statusStmnt = signal('');
  imessage = signal('');
  retro = signal(false);
  sending = signal(false);
  result = signal<{ ok: boolean; text: string } | null>(null);

  myProjects = signal<Array<{ pslot: string; project_name: string }>>([]);
  addressees = signal<Addressee[]>([]);
  suggestQuery = signal<string | null>(null);
  suggestIndex = signal(0);

  readonly suggestions = computed(() => {
    const q = this.suggestQuery();
    if (q === null) return [];
    const s = q.toLowerCase();
    return this.addressees()
      .filter((a) => a.mentionable && (a.handle!.includes(s) || a.display_name.toLowerCase().includes(s)))
      .slice(0, 8);
  });

  /** Wen der Appell tatsächlich nennt — nur Handles, die es im pslot gibt. */
  readonly recipients = computed(() => {
    const handles = new Set([...this.appeal().matchAll(MENTION_ALL)].map((m) => m[1].toLowerCase()));
    return this.addressees().filter((a) => a.handle && handles.has(a.handle));
  });

  readonly canSend = computed(() =>
    !!this.pslot() && this.recipients().length > 0 && this.appeal().trim().length > 0
    && this.statusStmnt().trim().length > 0 && this.imessage().trim().length > 0
    && (this.anchorType() === 'pslot' || (this.anchorId() ?? 0) > 0) && !this.sending());

  // ── Reiter „For me“ / „Waiting for“ ────────────────────────────────────
  waiting = signal<WaitingRequest[]>([]);
  replyFor = signal<number | null>(null);
  replyAnswer = signal('');
  replyNext = signal('');
  busy = signal<number | null>(null);

  constructor() {
    this.req.start();
    this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => { if (!this.req.panelOpen()) this.kontextAusAdresse(); });
    this.kontextAusAdresse();

    // Beim Öffnen: Kontext auffrischen, Listen laden.
    effect(() => {
      if (this.req.panelOpen()) {
        queueMicrotask(() => this.beimOeffnen(this.req.tab()));
      }
    });
  }

  // ── Tastatur ───────────────────────────────────────────────────────────
  @HostListener('document:keydown', ['$event'])
  onKey(e: KeyboardEvent): void {
    if (e.altKey && !e.ctrlKey && !e.metaKey && (e.key === 'p' || e.key === 'P' || e.code === 'KeyP')) {
      e.preventDefault();
      if (this.req.panelOpen()) this.req.closePanel(); else this.req.openPanel('ask');
      return;
    }
    if (e.key === 'Escape' && this.req.panelOpen()) {
      if (this.suggestQuery() !== null) { this.suggestQuery.set(null); return; }
      this.req.closePanel();
    }
  }

  toggle(): void {
    if (this.req.panelOpen()) this.req.closePanel();
    else this.req.openPanel(this.req.openCount() > 0 ? 'mine' : 'ask');
  }

  setTab(t: PanelTab): void { this.req.tab.set(t); this.beimOeffnen(t); }

  private beimOeffnen(t: PanelTab): void {
    if (t === 'ask') {
      this.kontextAusAdresse();
      const d = this.req.draft();
      if (d) { this.req.draft.set(null); this.entwurfUebernehmen(d); }
      if (!this.myProjects().length) this.ladeProjekte();
      setTimeout(() => this.appealBox?.nativeElement.focus(), 0);
    }
    if (t === 'mine') this.req.refresh();
    if (t === 'waiting') this.req.waiting().subscribe((w) => this.waiting.set(w));
  }

  /** pslot und Element aus der Adresse — /project/<pslot>/<typ>/<id>… */
  private kontextAusAdresse(): void {
    const m = this.router.url.match(/^\/project\/([^/?#]+)(?:\/(item|task|sprint|milestone)\/(\d+))?/);
    if (!m) return;
    const neu = decodeURIComponent(m[1]);
    if (neu !== this.pslot()) { this.pslot.set(neu); this.ladeAnsprechbare(); }
    if (m[2] && m[3]) { this.anchorType.set(m[2] as AnchorType); this.anchorId.set(Number(m[3])); }
    else { this.anchorType.set('pslot'); this.anchorId.set(null); }
  }

  /** Entwurf aus dem Eisenhower-Fenster einsetzen; Empfänger erst nach dem Laden der Ansprechbaren. */
  private entwurfUebernehmen(d: AskDraft): void {
    this.pslot.set(d.pslot);
    this.anchorType.set((ANCHOR_TYPES as readonly string[]).includes(d.anchorType) ? d.anchorType as AnchorType : 'pslot');
    this.anchorId.set(d.anchorType === 'pslot' ? null : d.anchorId);
    this.statusStmnt.set(d.statusStmnt);
    this.imessage.set(d.imessage);
    void this.agents.load(d.pslot);
    this.req.addressees(d.pslot).subscribe((liste) => {
      this.addressees.set(liste);
      const role = (d.to.role || '').toLowerCase();
      const a = liste.find((x) => x.mentionable && (d.to.uuid ? x.uuid === d.to.uuid
        : !!role && ((x.handle || '').toLowerCase().includes(role) || x.display_name.toLowerCase().includes(role))));
      const handle = a?.handle || role || (d.to.label || '').toLowerCase();
      const text = `@${handle} ${d.appeal}`;
      this.appeal.set(text);
      setTimeout(() => {
        if (this.appealBox) { this.appealBox.nativeElement.value = text; this.appealBox.nativeElement.focus(); }
      }, 0);
      if (!a) this.result.set({ ok: false, text: `No one with the role "${d.to.label || role}" in this project yet — choose a recipient with @.` });
    });
  }

  onPslotChange(p: string): void { this.pslot.set(p); this.anchorType.set('pslot'); this.anchorId.set(null); this.ladeAnsprechbare(); }

  private ladeAnsprechbare(): void {
    const p = this.pslot();
    if (!p) { this.addressees.set([]); return; }
    // Nur übernehmen, wenn der pslot noch derselbe ist (ein Entwurf kann ihn inzwischen gewechselt haben)
    this.req.addressees(p).subscribe((a) => { if (p === this.pslot()) this.addressees.set(a); });
    void this.agents.load(p);
  }

  /** Ampel eines Agenten im gewählten pslot; null bei Menschen oder solange unbekannt. */
  agentOf(a: { uuid: string; is_agent: boolean }): AgentStatus | null {
    return a.is_agent ? this.agents.of(this.pslot(), a.uuid) : null;
  }

  /** Genannte Agenten, die gerade nicht grün sind — die Anfrage wartet dann in der Warteschlange. */
  readonly notReady = computed(() => this.recipients()
    .map((r) => this.agentOf(r)).filter((s): s is AgentStatus => !!s && s.light !== 'green'));

  private ladeProjekte(): void {
    const host = window.location.hostname;
    const api = host.includes('localhost') || host === '127.0.0.1' ? 'http://localhost:3000' : `https://api.${host}`;
    this.http.get<{ activeProjects?: Array<{ pslot: string; project_name: string }> }>(
      `${api}/api/my_project_slots_from_redis`, { withCredentials: true })
      .subscribe({
        next: (r) => this.myProjects.set((r.activeProjects || []).map((p) => ({ pslot: p.pslot, project_name: p.project_name }))),
        error: () => this.myProjects.set([]),
      });
  }

  // ── @-Auswahl ──────────────────────────────────────────────────────────
  onAppealInput(el: HTMLTextAreaElement): void {
    this.appeal.set(el.value);
    const vorCaret = el.value.slice(0, el.selectionStart ?? el.value.length);
    const m = vorCaret.match(MENTION_AT_CARET);
    this.suggestQuery.set(m ? m[2] : null);
    this.suggestIndex.set(0);
  }

  onAppealKey(e: KeyboardEvent, el: HTMLTextAreaElement): void {
    const list = this.suggestions();
    if (this.suggestQuery() === null || !list.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); this.suggestIndex.set((this.suggestIndex() + 1) % list.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); this.suggestIndex.set((this.suggestIndex() - 1 + list.length) % list.length); }
    else if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); this.pick(list[this.suggestIndex()], el); }
  }

  pick(a: Addressee, el: HTMLTextAreaElement): void {
    const pos = el.selectionStart ?? el.value.length;
    const vor = el.value.slice(0, pos).replace(/@([a-z0-9._-]*)$/i, `@${a.handle} `);
    const neu = vor + el.value.slice(pos);
    el.value = neu;
    this.appeal.set(neu);
    this.suggestQuery.set(null);
    el.focus();
    el.selectionStart = el.selectionEnd = vor.length;
  }

  // ── Senden ─────────────────────────────────────────────────────────────
  async send(): Promise<void> {
    if (!this.canSend()) return;
    this.sending.set(true);
    this.result.set(null);
    try {
      const postId = await this.req.send({
        pslot: this.pslot(), anchorType: this.anchorType(),
        anchorId: this.anchorType() === 'pslot' ? 0 : (this.anchorId() ?? 0),
        appeal: this.appeal(), statusStmnt: this.statusStmnt(), imessage: this.imessage(), retro: this.retro(),
      });
      const an = this.recipients().map((a) => a.display_name).join(', ');
      this.result.set({ ok: true, text: `Sent to ${an} (post #${postId}). They will be reminded until they answer.` });
      this.appeal.set(''); this.statusStmnt.set(''); this.imessage.set(''); this.retro.set(false);
      if (this.appealBox) this.appealBox.nativeElement.value = '';
    } catch (e: any) {
      const missing = e?.error?.missing ? ` (missing: ${e.error.missing.join(', ')})` : '';
      this.result.set({ ok: false, text: `Not sent: ${e?.error?.message || e?.message || 'error'}${missing}` });
    } finally {
      this.sending.set(false);
    }
  }

  // ── For me ─────────────────────────────────────────────────────────────
  startReply(q: OpenRequest): void {
    this.replyFor.set(q.message_id); this.replyAnswer.set(''); this.replyNext.set('');
  }

  async sendReply(q: OpenRequest): Promise<void> {
    if (!q.post_id || !this.replyAnswer().trim() || !this.replyNext().trim()) return;
    this.busy.set(q.message_id);
    try {
      await this.req.reply({
        pslot: q.pslot, anchorType: q.anchor.type || 'pslot', anchorId: q.anchor.id ?? 0, postId: q.post_id,
        questionRepeat: q.cflx.appeal || q.title, answer: this.replyAnswer(), nextStep: this.replyNext(),
      });
      this.replyFor.set(null);
    } finally { this.busy.set(null); }
  }

  async closeReq(id: number, reason: 'done' | 'obsolete', fromWaiting = false): Promise<void> {
    this.busy.set(id);
    try {
      await this.req.close(id, reason);
      if (fromWaiting) this.req.waiting().subscribe((w) => this.waiting.set(w));
    } finally { this.busy.set(null); }
  }

  go(pslot: string, type: string | null, id: number | null): void {
    const link = anchorLink(pslot, type, id);
    if (link) { this.req.closePanel(); this.router.navigateByUrl(link); }
  }

  openRecipients(w: WaitingRequest): string {
    return w.recipients.filter((r) => r.state === 'open').map((r) => r.display_name).join(', ') || '—';
  }
}
